import {
  ArrowLeftRight,
  Landmark,
  MousePointerClick,
  Search,
  FileSearch,
  RefreshCw,
  Rocket,
} from "lucide-react";
import {
  PageHero,
  Container,
  SectionHead,
  FeatureGrid,
  CTASection,
  PrimaryButton,
} from "@/components/common/Section";
import { Reveal } from "@/lib/anim";
import EmulationFlow from "@/components/visuals/EmulationFlow";

const FEATURES = [
  {
    icon: <ArrowLeftRight className="h-5 w-5" />,
    title: "Bank transfer automation",
    desc: "Automate bank-transfer workflows end to end: initiation, status, files and reconciliation.",
  },
  {
    icon: <Landmark className="h-5 w-5" />,
    title: "Bank integration",
    desc: "Emulate bank hosts and channels so integrations behave predictably in every environment.",
  },
  {
    icon: <MousePointerClick className="h-5 w-5" />,
    title: "No-code configuration",
    desc: "Configure new banks and formats without engineering tickets or release cycles.",
  },
  {
    icon: <Search className="h-5 w-5" />,
    title: "Error detection",
    desc: "Detect format drift, broken flows and integration errors as they happen.",
  },
  {
    icon: <FileSearch className="h-5 w-5" />,
    title: "Error analysis",
    desc: "Isolate root causes automatically with field-level diagnostics and confidence scoring.",
  },
  {
    icon: <RefreshCw className="h-5 w-5" />,
    title: "Auto-healing",
    desc: "Regenerate parsers and mappings automatically, with regression checks before promotion.",
  },
  {
    icon: <Rocket className="h-5 w-5" />,
    title: "Production deployment",
    desc: "Promote healed configurations to production safely, with no downtime and a full audit trail.",
  },
];

const STEPS = [
  { num: "01", title: "Connect", desc: "Point the emulation layer at the bank host, channel or file format." },
  { num: "02", title: "Configure", desc: "Define formats, mappings and rules through no-code configuration." },
  { num: "03", title: "Automate", desc: "Transfers, statuses and reconciliation run without manual intervention." },
  { num: "04", title: "Heal", desc: "When banks change formats, the platform detects, analyzes, heals and deploys." },
];

export default function Emulation() {
  return (
    <>
      <PageHero
        eyebrow="Emulation Platform"
        title="Bank transfer automation, without the engineering backlog."
        copy="Automate bank-transfer workflows and reduce dependency on engineering teams through intelligent bank emulation technology, with no-code configuration and self-healing integrations."
        testid="emulation-page-title"
      >
        <Reveal delay={120} className="mt-9 flex flex-col gap-4 sm:flex-row">
          <PrimaryButton to="/contact" testid="emulation-hero-cta">
            Talk to Our Payments Team
          </PrimaryButton>
        </Reveal>
      </PageHero>

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHead
            eyebrow="How It Works"
            title="Bank → Automation Layer → Payment Flow."
            copy="The emulation engine sits between banks and your payment flows, absorbing format changes and operational complexity."
          />
          <div className="mt-14">
            <EmulationFlow />
          </div>
        </Container>
      </section>

      <section className="border-y border-[#E2E8F0] bg-white py-20 lg:py-24">
        <Container>
          <SectionHead
            eyebrow="Capabilities"
            title="Integrations that maintain themselves."
            copy="Bank integrations drift. The Emulation Platform keeps them running."
          />
          <div className="mt-14">
            <FeatureGrid items={FEATURES} cols={4} testidPrefix="emulation-feature" />
          </div>
        </Container>
      </section>

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHead eyebrow="Operating Model" title="From connection to self-healing in four steps." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal
                key={s.num}
                delay={i * 80}
                data-testid={`emulation-step-${i}`}
                className="rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
              >
                <span className="font-mono-brand text-sm font-medium text-[#2563EB]">{s.num}</span>
                <h3 className="mt-3 text-lg font-semibold text-[#1D305B]">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#64748B]">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CTASection
        title="Spending too much engineering time on bank integrations?"
        copy="Show us your transfer workflows and we'll show you what automation looks like."
      />
    </>
  );
}
